import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import {
  global,
  side_logout,
  side_asset,
  side,
  other,
  newReport,
  SkipAgreeModal,
  Item,
} from 'config/Routes';
import * as env from 'config/environment';
import * as consts from 'config/consts';
import { useLoading } from 'hooks/useLoadign';
import { getProxyAgent } from 'utils/utils';
import { WithAgreeModal } from 'routes/components/AgreeModal';
import { AdvertisementModal } from 'routes/components/AdvertisementModal';
import { RecurrentModal, ModalID } from 'routes/components/RecurrentModal';
import { NotFound } from 'components/features/NotFound';
import {
  getUserStorage,
  isFirstTimeLogin,
} from 'components/features/utils/utils';
import Loading from 'components/commons/loading/Loading';
import { isApp, isAndroidApp } from 'components/features/utils/userStatus';

type AuthState = 'unloaded' | 'success' | 'failure';

export const useAuthCheck = () => {
  const [authState, setAuthState] = useState<AuthState>('unloaded');

  useEffect(() => {
    if (getProxyAgent()) {
      setAuthState('success');
    } else {
      /*
        認証チェックのみ行う
        状態を変更する副作用はなし
      */
      (async () => {
        try {
          const res = await fetch(
            `${env.appURL}/authcheck?t=${new Date().getTime()}`,
            {
              method: 'GET',
            }
          );
          if (res.status !== 200 || !getUserStorage()) {
            setAuthState('failure');
            return;
          }
          setAuthState('success');
        } catch {
          setAuthState('failure');
        }
      })();
    }
  }, []);
  return { authState };
};

// TODO: MK_TW2-1240で追加されるiosアプリ判定のコードを引用している。
// iosアプリがリリースされたら、こちらは削除する。
const isIosApp =
  isApp &&
  /iphone|ipod|ipad|macintosh/.test(window.navigator.userAgent.toLowerCase());

const useAuthenticate = () => {
  const { authState } = useAuthCheck();
  const location = useLocation();

  const redirectAuthUrl = `${env.loginURL}?service=${
    env.serviceName
  }&callback_url=${encodeURI(
    `${env.appURL}/auth?callbackPath=${location.pathname + location.search}`
  )}`;
  const redirect = () => {
    if (isIosApp) {
      window.location.href = `${env.appURL}`;
    } else {
      window.location.href = redirectAuthUrl;
    }
  };
  useEffect(() => {
    if (authState === 'failure') redirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);
  return { isAuthenticated: authState === 'success' };
};

const AuthCheckWrapperComponent = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => {
  const { isAuthenticated } = useAuthenticate();
  if (!isAuthenticated || !children) return <div />;
  return <>{children}</>;
};

export type ModalTypeProps = 'agree' | 'advertisement';

const modalID = 'androidReview' as ModalID;

const ModalWrapperComponent = ({
  skipAgreeModal,
  children,
}: PropsWithChildren<{
  skipAgreeModal: SkipAgreeModal;
}>): React.ReactElement => {
  const location = useLocation();
  const [modalType, setModalType] = useState<ModalTypeProps>('agree');

  switch (skipAgreeModal) {
    case 'later':
      return <>{children}</>;
    case 'loading':
      return <Loading loading style={{ marginTop: '3rem' }} />;
    case 'show':
      return (
        <>
          {modalType === 'agree' ? (
            <>
              {!location.pathname.startsWith('/accountlinkageinfo') &&
              !location.pathname.startsWith('/notificationDetail/3') ? (
                <>
                  <WithAgreeModal setModalType={setModalType} />
                  {children}
                </>
              ) : (
                <>{children}</>
              )}
            </>
          ) : (
            <>
              {!location.pathname.startsWith('/accountlinkageinfo') &&
              !location.pathname.startsWith('/investoridexplanation') &&
              !location.pathname.startsWith('/investoridregistrationerror') &&
              !location.pathname.startsWith('/importantnotification') ? (
                <>
                  <AdvertisementModal />
                  {isAndroidApp() && <RecurrentModal {...{ modalID }} />}
                  {children}
                </>
              ) : (
                <>{children}</>
              )}
            </>
          )}
        </>
      );
  }
};

function Children({ item }: { item: Item }) {
  const Component = item.component ? item.component : () => null;

  const useSkipAgreeModal = item.useSkipAgreeModal ?? (() => 'show');
  const skipAgreeModal: SkipAgreeModal = useSkipAgreeModal();

  return (
    <AuthCheckWrapperComponent>
      <ModalWrapperComponent skipAgreeModal={skipAgreeModal}>
        <Component />
      </ModalWrapperComponent>
    </AuthCheckWrapperComponent>
  );
}

const Main: React.SFC = () => {
  const [loading] = useLoading();
  const location = useLocation();

  const user = getUserStorage();
  // MEMO: MONO障害対応中は資産データリンク登録画面への遷移判定を停止する
  const isNewUserAndInverstorIDNotIssued =
    !consts.IS_MONO_SUSPENDED &&
    user !== null &&
    user.NewUserAndInverstorIDNotIssued;

  const contens = global
    .concat(side_logout, side_asset, side, other, newReport)
    .map(item => {
      return (
        <Route
          path={item.path}
          exact={item.exact === undefined ? true : item.exact}
          key={item.path}>
          {!isNewUserAndInverstorIDNotIssued || isFirstTimeLogin() ? (
            <Children {...{ item }} />
          ) : (
            <>
              {!location.pathname.startsWith('/investor') &&
              !location.pathname.startsWith('/notificationDetail/3') &&
              !location.pathname.startsWith('/importantnotification') ? (
                <Redirect to="/investoridexplanation" />
              ) : (
                <Children {...{ item }} />
              )}
            </>
          )}
        </Route>
      );
    });

  return (
    <div
      className={`${
        location.pathname === '/home' ? 'home_container' : 'container'
      }`}
      id="id_container"
      style={{
        display: loading.loading ? 'none' : undefined,
      }}>
      <Switch>
        {contens}
        <Route path="*" component={NotFound} />
      </Switch>
    </div>
  );
};

export default Main;
